import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CalendarDate, daysPerYear } from '@datapad/data-model';
import { Add, Check, Close, Edit } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import React from 'react';
import { IntegerField } from './IntegerField.js';
export function InlineCalendarDateField(props) {
    const { date, onChange, canEdit = false, label } = props;
    const [isEditing, setIsEditing] = React.useState(false);
    const [editYear, setEditYear] = React.useState(date?.year);
    const [editDay, setEditDay] = React.useState(date?.day);
    React.useEffect(() => {
        if (!isEditing) {
            setEditYear(date?.year);
            setEditDay(date?.day);
        }
    }, [date, isEditing]);
    const handleStartEdit = React.useCallback(() => {
        if (canEdit) {
            setEditYear(date?.year);
            setEditDay(date?.day);
            setIsEditing(true);
        }
    }, [canEdit, date]);
    const isValidYear = editYear !== undefined && Number.isInteger(editYear) && editYear >= 0;
    const isValidDay = editDay !== undefined && Number.isInteger(editDay) && editDay >= 0 && editDay < daysPerYear;
    const canSave = isValidYear && isValidDay;
    const handleSave = React.useCallback(() => {
        if (canSave) {
            const newDate = CalendarDate.create(editYear, editDay);
            onChange(newDate);
        }
        setIsEditing(false);
    }, [canSave, editYear, editDay, onChange]);
    const handleCancel = React.useCallback(() => {
        setEditYear(date?.year);
        setEditDay(date?.day);
        setIsEditing(false);
    }, [date]);
    const handleKeyDown = React.useCallback((event) => {
        if (event.key === 'Escape') {
            handleCancel();
        }
        else if (event.key === 'Enter' && canSave) {
            event.preventDefault();
            handleSave();
        }
    }, [handleCancel, handleSave, canSave]);
    if (isEditing) {
        return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, onKeyDown: handleKeyDown, children: [_jsx(IntegerField, { value: editYear, label: "Year", onChange: setEditYear, min: 0 }), _jsx(IntegerField, { value: editDay, label: "Day", onChange: setEditDay, min: 0, max: daysPerYear - 1 })] }), _jsxs(Box, { sx: { display: 'flex', gap: 0.5 }, children: [_jsx(IconButton, { size: "small", onClick: handleSave, color: "primary", "aria-label": "save", disabled: !canSave, children: _jsx(Check, { fontSize: "small" }) }), _jsx(IconButton, { size: "small", onClick: handleCancel, "aria-label": "cancel", children: _jsx(Close, { fontSize: "small" }) })] })] }));
    }
    if (!date) {
        if (!canEdit) {
            return (_jsxs(Typography, { variant: "body2", color: "text.disabled", sx: { fontStyle: 'italic' }, children: [label ? `${label}: ` : '', "Not set"] }));
        }
        return (_jsxs(Box, { sx: {
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                },
                padding: 0.5,
                marginLeft: -0.5,
                borderRadius: 1,
            }, onClick: handleStartEdit, children: [_jsx(Typography, { variant: "body2", color: "text.disabled", sx: { fontStyle: 'italic', flexGrow: 1 }, children: label ? `Add ${label.toLowerCase()}` : 'Add date' }), _jsx(Add, { fontSize: "small", sx: {
                        color: 'action.active',
                        opacity: 0.5,
                        '&:hover': { opacity: 1 },
                    } })] }));
    }
    return (_jsxs(Box, { sx: {
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: canEdit ? 'pointer' : 'default',
            '&:hover': canEdit
                ? {
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                }
                : {},
            padding: 0.5,
            marginLeft: -0.5,
            borderRadius: 1,
        }, onClick: canEdit ? handleStartEdit : undefined, children: [_jsxs(Typography, { variant: "body2", component: "span", sx: { flexGrow: 1 }, children: [label && _jsxs("strong", { children: [label, ": "] }), date.toShortString()] }), canEdit && (_jsx(Edit, { fontSize: "small", sx: {
                    color: 'action.active',
                    opacity: 0.5,
                    '&:hover': { opacity: 1 },
                } }))] }));
}
//# sourceMappingURL=InlineCalendarDateField.js.map